import { createClient } from "@liveblocks/client";
import { LiveMap } from "@liveblocks/core";
import CONFIG from "config/config";
import { buildLiveblocksAuthEndpoint, buildLiveblocksUserPayload } from "pages/flyer/builder/utils/liveblocksAuth";
import { authorization } from "module/auth";

// We deliberately don't import from `pages/flyer/builder/utils.tsx` or any
// `pages/flyer/builder/drawer/*` file because Tldraw.tsx (which imports
// this helper) lives under that barrel — that produced a circular import
// that blanked the page on boot. Anything we need we import from
// `module/*` or recreate locally.

type TLStoreRecord = {
  id: string;
  typeName?: string;
  [key: string]: any;
};

const collectRecords = (editor: any): TLStoreRecord[] => {
  if (editor?.store?.allRecords) {
    try {
      return editor.store.allRecords() as TLStoreRecord[];
    } catch {
      /* fall through */
    }
  }
  const recordsMap = editor?.store?.records;
  if (recordsMap && typeof recordsMap === "object") {
    return Object.values(recordsMap) as TLStoreRecord[];
  }
  return [];
};

// Mirrors `getRoomId(designId)` for the authed user case (no guest segment).
const buildAuthedRoomId = (designId: string | number): string => {
  if (CONFIG.ENV === "production") {
    return `029_${CONFIG.ENV}_${designId}`;
  }
  return `${CONFIG.ENV}_${designId}`;
};

/**
 * Copy the guest's in-progress canvas to the authed Liveblocks room so
 * the design opens populated when the user lands on the authed builder
 * URL after signup. The campaign-builder uses tldraw v3 which keeps state
 * in a Liveblocks `LiveMap` at `storage.root.get("records")`. We connect
 * to the brand-new authed room, write the records map, and disconnect.
 *
 * Best-effort: failures are logged but don't block signup.
 */
export const copyGuestStateToAuthedRoom = async (editor: any, newDesignId: string | number): Promise<boolean> => {
  if (!editor || !newDesignId) return false;

  const records = collectRecords(editor);
  if (records.length === 0) return false;

  const authEndpointUrl = process.env.REACT_APP_LIVEBLOCKS_AUTH_URL;
  if (!authEndpointUrl) {
    // eslint-disable-next-line no-console
    console.warn("[copyGuestStateToAuthedRoom] REACT_APP_LIVEBLOCKS_AUTH_URL missing");
    return false;
  }

  const targetRoomId = buildAuthedRoomId(newDesignId);

  // Use the now-authed user's identity so the Liveblocks auth endpoint
  // grants FULL_ACCESS to the new authed-namespaced room.
  const userPayload = buildLiveblocksUserPayload(authorization.getUser?.());
  const authEndpoint = buildLiveblocksAuthEndpoint(authEndpointUrl, userPayload);

  let leaveRoom: (() => void) | null = null;
  try {
    const liveblocks = createClient({ authEndpoint });
    const enter = liveblocks.enterRoom(targetRoomId, {
      initialPresence: { presence: undefined } as any,
      initialStorage: { records: new LiveMap() } as any,
    });
    const room = (enter as any).room;
    leaveRoom = (enter as any).leave;

    const storage = await room.getStorage();
    let liveRecords = storage.root.get("records") as any;
    if (!liveRecords) {
      liveRecords = new LiveMap();
      storage.root.set("records", liveRecords);
    }

    // Defensive clean before the carry-over: while the authed room is
    // usually brand-new (we just claimed `newDesignId` via the API),
    // an existing room could land here in edge cases — a retried
    // claim, a races against another tab, a manual id collision in
    // testing, or future flows that reuse this helper for an
    // already-populated room. Mirroring the "+ New Design" behavior
    // (which wipes guest-placeholder shapes before re-seeding), we
    // empty `liveRecords` so what the user sees post-redirect is
    // exactly the carry-over snapshot, never carry-over merged on top
    // of pre-existing state. LiveMap has no `.clear()` — iterate the
    // current keys (materialized via `Array.from` so the iteration
    // isn't invalidated by the deletes that follow).
    for (const key of Array.from(liveRecords.keys() as IterableIterator<string>)) {
      liveRecords.delete(key);
    }

    // Write every record from the guest editor into the new authed room.
    for (const record of records) {
      if (!record?.id) continue;
      liveRecords.set(record.id, record);
    }

    // CRITICAL: wait for Liveblocks to push our writes to the server before
    // we return / the caller redirects. Without this, the page navigation
    // tears down the WebSocket while the changes are still buffered
    // client-side and we lose everything we just wrote.
    await new Promise<void>((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        resolve();
      };

      // Modern Liveblocks: subscribe to storage-status changes.
      try {
        if (typeof room.getStorageStatus === "function") {
          if (room.getStorageStatus() === "synchronized") {
            finish();
            return;
          }
          if (typeof room.subscribe === "function") {
            const unsub = room.subscribe("storage-status", (status: string) => {
              if (status === "synchronized") {
                try {
                  unsub();
                } catch {
                  /* ignore */
                }
                finish();
              }
            });
          }
        }
      } catch {
        /* ignore — falls through to timeout */
      }

      // Safety net: never block the signup flow longer than 4s.
      setTimeout(finish, 4000);
    });

    return true;
  } catch (err: any) {
    // eslint-disable-next-line no-console
    console.warn("[copyGuestStateToAuthedRoom] failed:", err?.message || err);
    return false;
  } finally {
    try {
      if (leaveRoom) leaveRoom();
    } catch {
      /* ignore */
    }
  }
};
